import setAddedToCartModalOpen from "@iewccouk/Store/Handlers/Custom/CustomSetAddedToCartModal";
import {
    addProtonAddToCart,
    addProtonDataLayerSession,
    setGA4Event,
} from "@iewccouk/Store/Handlers/Utilities/CustomTracking";
import { isQuoteRequired } from "@iewccouk/Store/Handlers/Utilities/useCustomQuoteRequired";
import { EagerLoadingLogoProvider } from "@insite/client-framework/Common/EagerLoadingLogo";
import { createFromProduct, ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import loadRealTimeInventory from "@insite/client-framework/Store/CommonHandlers/LoadRealTimeInventory";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import dismissRfqUpdates from "@insite/client-framework/Store/Context/Handlers/DismissRfqUpdates";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import loadProduct from "@insite/client-framework/Store/Data/Products/Handlers/LoadProduct";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import addToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddToCart";
import translate from "@insite/client-framework/Translate";
import { CartLineModel, ProductModel, RealTimeInventoryModel } from "@insite/client-framework/Types/ApiModels";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import ProductAddedToCartMessage from "@insite/content-library/Components/ProductAddedToCartMessage";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Link from "@insite/mobius/Link";
import Page, { PageProps as MobiusPageProps } from "@insite/mobius/Page";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);
    const currentBillTo = getBillToState(state, session.billToId).value;
    const customerNumber =
        session.billToId == null
            ? "__USER_IS_GUEST__"
            : currentBillTo?.customerNumber?.replace(/^0+/, "") ?? "__IS_LOADING__";

    return {
        siteName: state.context.website.name,
        session,
        myAccountPageUrl: getPageLinkByPageType(state, "MyAccountPage")?.url,
        currentLocation: getLocation(state),
        showAddedToCartModal: (getSettingsCollection(state) as any).customCartSettings?.showCustomAddToCartModal === true,
        // Verndale Customization
        customerNumber,
    };
};

const mapDispatchToProps = {
    dismissRfqUpdates,
    // Verndale Customization - For Proton AI
    addToCart: makeHandlerChainAwaitable<Parameters<typeof addToCart>[0], CartLineModel>(addToCart),
    loadProduct: makeHandlerChainAwaitable<Parameters<typeof loadProduct>[0], ProductModel>(loadProduct),
    loadRealTimeInventory: makeHandlerChainAwaitable<
        Parameters<typeof loadRealTimeInventory>[0],
        RealTimeInventoryModel
    >(loadRealTimeInventory),
    setAddedToCartModalOpen,
};

type Props = PageProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext &
    HasHistory;

export interface HeaderStyles {
    page?: MobiusPageProps;
    container?: InjectableCss;
    skipToContent?: SkipNavStyles;
    websiteName?: InjectableCss;
}

export const headerStyles: HeaderStyles = {
    page: {
        fullWidth: [true, true, false, false, false],
    },
    container: {
        css: css`
            background-color: ${getColor("secondary.main")};
            display: flex;
            > div:first-child {
                flex-grow: 1;
            }
            > div:last-child {
                max-width: 150px;
                display: flex;
                align-items: center;
                justify-content: center;
                height: auto;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                max-width: 100px;
                            `,
                        ],
                        "max",
                    )}
            }
        `,
    },
    websiteName: {
        css: css`
            @media print {
                display: block;
            }
            font-size: 12px;
            padding-left: 15px;
            display: none;
        `,
    },
};

const styles = headerStyles;

const useReactPath = () => {
    const [path, setPath] = React.useState(window.location.pathname);
    const listenToPopstate = () => {
        const winPath = window.location.pathname;
        setPath(winPath);
    };
    useEffect(() => {
        window.addEventListener("popstate", listenToPopstate);
        return () => {
            window.removeEventListener("popstate", listenToPopstate);
        };
    }, []);
    return path;
};

const Header = ({
    id,
    siteName,
    session,
    toaster,
    history,
    myAccountPageUrl,
    dismissRfqUpdates,
    currentLocation,
    showAddedToCartModal,
    addToCart,
    loadProduct,
    loadRealTimeInventory,
    setAddedToCartModalOpen,
    // Verndale Customization
    customerNumber,
}: Props) => {
    const viewMyDashboardClickHandler = () => {
        if (myAccountPageUrl) {
            history.push(myAccountPageUrl);
        }
    };
    const openAddedToCartConfirmation = async (cartline: CartLineModel) => {
        if (showAddedToCartModal) {
            // Match PDP: load product + inventory so availability (lead time / NCNR) renders correctly.
            let product: ProductModel | undefined;
            let productInfo: ProductInfo | undefined;
            const propertyKeys = ["isQuotedByUser", "looseEnds", "isCustNCNR", "isBackOrder", "deliveryLeadTimeWeeks"];

            if (cartline.productId) {
                try {
                    product = (await loadProduct({ id: cartline.productId })) as ProductModel | undefined;
                } catch {
                    product = undefined;
                }
            }

            const availabilityProps: Record<string, string> = {};
            propertyKeys.forEach(key => {
                const productValue = product?.properties?.[key];
                const cartLineValue = cartline.properties?.[key];
                const value =
                    `${productValue ?? ""}`.toLowerCase() === "true"
                        ? productValue
                        : `${cartLineValue ?? ""}`.toLowerCase() === "true"
                          ? cartLineValue
                          : productValue ?? cartLineValue;
                if (value != null && value !== "") {
                    availabilityProps[key] = value;
                }
            });

            const quoteRequired =
                isQuoteRequired(
                    product ? { product, productInfo: createFromProduct(product) } : null,
                ) ||
                !!cartline.quoteRequired ||
                `${cartline.properties?.["isQuotedByUser"] ?? ""}`.toLowerCase() === "true";

            if (quoteRequired) {
                availabilityProps.isQuotedByUser = "true";
            }

            if (product) {
                product = {
                    ...product,
                    quoteRequired: quoteRequired || !!product.quoteRequired,
                    properties: {
                        ...product.properties,
                        ...availabilityProps,
                    },
                };
                const baseProductInfo = createFromProduct(product);
                productInfo = {
                    ...baseProductInfo,
                    qtyOrdered: cartline.qtyOrdered ?? baseProductInfo.qtyOrdered,
                    unitOfMeasure: cartline.unitOfMeasure || baseProductInfo.unitOfMeasure,
                };
                try {
                    const inventoryResult = (await loadRealTimeInventory({
                        productIds: [product.id],
                    })) as RealTimeInventoryModel | undefined;
                    productInfo.inventory = inventoryResult?.realTimeInventoryResults?.find(
                        o => o.productId === product!.id,
                    );
                } catch {
                    productInfo.failedToLoadInventory = true;
                }
            }

            setAddedToCartModalOpen({
                isOpen: true,
                cartLine: {
                    ...cartline,
                    quoteRequired: quoteRequired || !!cartline.quoteRequired,
                    properties: {
                        ...cartline.properties,
                        ...availabilityProps,
                    },
                },
                originalQtyOrdered: cartline.qtyOrdered ?? undefined,
                isQtyAdjusted: cartline.isQtyAdjusted,
                product,
                productInfo,
            });
        } else {
            toaster.addToast({
                body: <ProductAddedToCartMessage isQtyAdjusted={cartline.isQtyAdjusted} multipleProducts={false} />,
                messageType: "success",
                timeoutLength: 3000,
            });
        }
    };
    let isFirstLoad = true;

    // Verndale Customization
    useEffect(() => {
        addProtonAddToCart(addToCart, openAddedToCartConfirmation);
    }, []);

    useEffect(() => {
        // we save currentLocation.pathname to local storage
        // so that we can use it to compare with the next location.pathname

        const previousLocation = localStorage.getItem("previousLocation");

        if (previousLocation !== currentLocation?.pathname) {
            localStorage.setItem("previousLocation", currentLocation?.pathname);
        }

        if (currentLocation.pathname !== previousLocation || isFirstLoad) {
            isFirstLoad = false;
            addProtonDataLayerSession("user_info", session);
            addProtonAddToCart(addToCart, openAddedToCartConfirmation);
            if (session && session.isAuthenticated && customerNumber) {
                const userID = session.userProfileId;

                setGA4Event([], "session", { userID, sapAccountNumber: customerNumber });
            }
        }
    }, [currentLocation, session]);

    useEffect(() => {
        if (session.hasRfqUpdates && myAccountPageUrl) {
            toaster.addToast({
                body: (
                    <>
                        <Typography>{translate("You have new quote information.")}</Typography>
                        <Link onClick={viewMyDashboardClickHandler}>{translate("View My Dashboard")}</Link>
                    </>
                ),
                messageType: "info",
                timeoutLength: 1000 * 60 * 60 * 24 * 365,
                onClose: () => {
                    dismissRfqUpdates();
                },
            });
        }
    }, [myAccountPageUrl]);

    const afterHeader = React.createRef<HTMLSpanElement>();
    return (
        <>
            {siteName && <Typography {...styles.websiteName}>{siteName}</Typography>}
            <Page as="header" isHeader={true} {...styles.page}>
                <SkipNav
                    extendedStyles={styles.skipToContent}
                    text={translate("Skip to main content")}
                    destination={afterHeader}
                />
                <EagerLoadingLogoProvider value={true}>
                    <Zone contentId={id} zoneName="SecondaryNavigation" fixed />
                    <StyledWrapper {...styles.container}>
                        <Zone contentId={id} zoneName="MainNavigation" fixed />
                    </StyledWrapper>
                    <Zone contentId={id} zoneName="Breadcrumb" />
                </EagerLoadingLogoProvider>
                <span ref={afterHeader} tabIndex={-1} />
            </Page>
        </>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withToaster(withHistory(Header))),
    definition: {
        hasEditableTitle: false,
        hasEditableUrlSegment: false,
        pageType: "System",
    },
};

export const HeaderContext = "HeaderContext";

export default pageModule;
